import { useState } from 'react';
import { useCart, IAppliedCoupon } from '../hooks/useCart';
import { useLang } from '../hooks/useLanguage';
import { EMAIL_REGEX } from '../helpers/validation';
import { validateCoupon } from '../../openapi/coupons/coupons';

interface CouponInputProps {
    showId: number;
    email: string;
    orderAmount: number;
}

function CouponInput({ showId, email, orderAmount }: CouponInputProps) {
    const { t } = useLang();
    const { appliedCoupon, setAppliedCoupon, clearCoupon } = useCart();
    const [couponCode, setCouponCode] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;

        if (!EMAIL_REGEX.test(email)) {
            setError(t('coupons.emailRequired'));
            return;
        }

        setIsValidating(true);
        setError(null);

        try {
            const result = await validateCoupon({
                code: couponCode.trim().toUpperCase(),
                showId,
                customerEmail: email,
                orderAmount,
            });

            if (result.valid && result.discountType && result.discountValue !== undefined) {
                const appliedCouponData: IAppliedCoupon = {
                    code: couponCode.trim().toUpperCase(),
                    discountType: result.discountType as 'percentage' | 'fixed',
                    discountValue: result.discountValue,
                    discountAmount: result.discountAmount || 0,
                    minOrderAmount: result.minOrderAmount || undefined,
                };
                setAppliedCoupon(appliedCouponData);
                setCouponCode('');
            } else {
                setError(result.message || t('coupons.invalid'));
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || t('coupons.invalid'));
        } finally {
            setIsValidating(false);
        }
    };

    const handleRemoveCoupon = () => {
        clearCoupon();
        setError(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && couponCode.trim() && !isValidating) {
            e.preventDefault();
            handleApplyCoupon();
        }
    };

    if (appliedCoupon) {
        return (
            <div className="fireknife-coupon fireknife-coupon--applied">
                <div className="fireknife-coupon__applied">
                    <span className="fireknife-coupon__code">{appliedCoupon.code}</span>
                    <span className="fireknife-coupon__discount">
                        {appliedCoupon.discountType === 'percentage'
                            ? `-${appliedCoupon.discountValue}%`
                            : `-$${(appliedCoupon.discountAmount / 100).toFixed(2)}`}
                    </span>
                    <button
                        type="button"
                        className="fireknife-coupon__remove fireknife-btn"
                        onClick={handleRemoveCoupon}
                        aria-label={t('coupons.remove')}
                    >
                        &times;
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fireknife-coupon">
            <div
                className={`fireknife-form-control-box fireknife-coupon__input-wrapper ${error ? 'fireknife-form-control-box--error' : ''}`}
            >
                <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyDown={handleKeyDown}
                    placeholder={t('coupons.enterCode')}
                    className="fireknife-form-control fireknife-coupon__input"
                    disabled={isValidating}
                />
                <button
                    type="button"
                    className="fireknife-coupon__apply fireknife-btn fireknife-btn--primary"
                    onClick={handleApplyCoupon}
                    disabled={isValidating || !couponCode.trim()}
                >
                    {isValidating ? '...' : t('coupons.apply')}
                </button>
            </div>
            {error && <div className="fireknife-form-control-box--error-message">{error}</div>}
        </div>
    );
}

export default CouponInput;
